import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { ApolloProvider, gql } from '@apollo/client';
import { useV2Query } from 'utils';
import { graphqlClient } from 'bk-web-app';
import ReactMarkdown from 'react-markdown';
import getClientId from './../getClientId';
import { LoadingPlaceholder } from '../../client-web-app/components';
import { NetworkStatus } from '@apollo/client';

import RefreshIcon from '@material-ui/icons/Refresh';

const HeaderContainer = styled.div`
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  overflow: hidden;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  width: 100%;
`;

const TopBar = styled.div`
  background-color: #43918b; /* Teal-green top section */
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.6em 1.25em;
`;

const SecondaryBar = styled.div`
  background-color: #007a74;
  padding: 0.6em 1.25em;
  margin-right: 0;
  position: relative;
  color: #fff;
`;

const CompanyName = styled.h2`
  margin: 0;
  font-size: 20px;
`;

const RefreshButton = styled.button`
  display: inline-flex;
  align-items: center;
  border: none;
  background: none;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background 0.2s, color 0.2s;
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: white;
  }
  .refresh-icon {
    transition: transform 0.4s;
  }
  &.spinning .refresh-icon {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const SummaryLayout = styled.div`
  display: grid;
  gap: 8px;
  border: 1px solid #c6c6c6;
  margin: 10px auto;
  border-radius: 8px;
  text-align: left;
  margin-bottom: 50px;
  max-width: 900px;
  h3 {
    font-size: 20px;
    font-weight: 500;
    padding-left: 10px;
  }
`;

const Summary = styled.div`
  padding: 20px;
  /* Reset list styles for ul/ol inside summary to default browser styles */
  ul,
  ol {
    list-style: initial;
    margin-left: 2em;
    padding-left: revert;
    margin-top: revert;
    margin-bottom: revert;
  }
  li {
    list-style-type: revert;
    margin: revert;
    padding: revert;
  }
`;

const GET_CLIENT_SUMMARY = gql`
  query GetClientSummary($clientId: ID!, $isRefreshRequired: Boolean) {
    clientSummary(clientId: $clientId, isRefreshRequired: $isRefreshRequired) {
      summaryText
      lastUpdated
    }
  }
`;

const ClientSummaryContent: React.FC = () => {
  const clientId = useMemo(() => getClientId(), []);
  const { data, loading, error, refetch, networkStatus } = useV2Query(GET_CLIENT_SUMMARY, {
    variables: { clientId, isRefreshRequired: false },
    fetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
  });

  const handleRefresh = useCallback(() => {
    refetch({ clientId, isRefreshRequired: true });
  }, [clientId, refetch]);

  const isLoading = loading || networkStatus === NetworkStatus.refetch;

  return (
    <SummaryLayout>
      <HeaderContainer>
        <TopBar>
          <CompanyName>Client Summary</CompanyName>
          <RefreshButton
            className={`action-button invert ${isLoading ? 'spinning' : ''}`}
            onClick={handleRefresh}
            title="Refresh Client Summary"
            aria-label="Refresh Client Summary"
            disabled={isLoading}
          >
            <RefreshIcon className="refresh-icon" />
          </RefreshButton>
        </TopBar>
        <SecondaryBar>
          Last updated:{' '}
          {data?.clientSummary ? (
            <>{new Date(data.clientSummary.lastUpdated).toLocaleDateString()}</>
          ) : (
            'Loading...'
          )}
        </SecondaryBar>
      </HeaderContainer>
      {isLoading ? (
        <LoadingPlaceholder minHeight={500} />
      ) : error ? (
        <div>Error: {error.message}</div>
      ) : (
        <Summary>
          <ReactMarkdown>{data?.clientSummary?.summaryText ?? 'No summary found.'}</ReactMarkdown>
        </Summary>
      )}
    </SummaryLayout>
  );
};

const ClientSummary: React.FC = () => (
  <ApolloProvider client={graphqlClient}>
    <ClientSummaryContent />
  </ApolloProvider>
);

export default ClientSummary;
